import { Container, Graphics } from "pixi.js";
import { AIM, COLORS, KICK, type Ball, type Player } from "@golazo/shared";
import type { KickKind } from "../game/kick.js";
import { humanChargeCurve, shouldCross } from "../game/kick.js";
import { previewKick, type KickPreview } from "../game/kickPreview.js";
import { lerp } from "../core/math.js";

export interface AimLayerOptions {
  /** El balon del partido: solo se lee su radio para simular el vuelo. */
  ball: Pick<Ball, "radius">;
  /** La asistencia del partido (`AssistProfile.spreadScale`): cambia el golpeo cruzado. */
  assistSpreadScale?: number;
}

/** Marca de la porteria: entra, palo, fuera. */
const MARK_GOAL = 0x7cffb2;
const MARK_POST = 0xffb74d;
const MARK_WIDE = 0xff6b6b;

export interface AimLayer {
  container: Container;
  /** Fija la direccion bloqueada al empezar a cargar K. */
  lockShot(x: number, y: number, kind: KickKind): void;
  clearShotLock(): void;
  /**
   * Ventana de golpeo de primera abierta: `quality` 0-1 y `t` segundos que
   * faltan para el contacto. `null` la cierra.
   */
  setFirstTime(state: { quality: number; timeToContact: number } | null): void;
  sync(alpha: number): void;
}

/**
 * Mira del jugador humano. Al cargar K, si rediriges con WASD aparece una
 * flecha (solo tu la ves) hacia el nuevo angulo. Ya no hay reticle fijo: la
 * tecla de apuntar se quito, y la direccion se lee de la flecha de carga.
 */
export function createAimLayer(player: Player, options: AimLayerOptions): AimLayer {
  const target = player;
  const layer = new Container();
  layer.label = "aim";

  /*
   * El resto del vuelo y la marca de la porteria, en coordenadas del CAMPO
   * (fuera de `root`, que va pegado al jugador): la linea de gol no se mueve
   * con el.
   */
  const course = new Graphics();
  course.visible = false;
  layer.addChild(course);

  const root = new Container();
  root.visible = false;
  layer.addChild(root);

  /**
   * Anillo de "primera". Va fuera de `root` porque tiene que verse sin estar
   * cargando: es justo el gesto que se hace sin tiempo de preparar nada.
   *
   * Se cierra sobre el jugador conforme el balon llega y destella cuando el
   * contacto seria perfecto. Sin esta señal la mecanica es invisible y el
   * timing solo se puede aprender a base de fallar sin saber por que.
   */
  const firstTimeRing = new Graphics();
  firstTimeRing.visible = false;
  layer.addChild(firstTimeRing);
  let firstTime: { quality: number; timeToContact: number } | null = null;

  // Flecha de tiro al redirigir mientras cargas (solo humano).
  const shotArrow = new Graphics();
  shotArrow.visible = false;
  root.addChild(shotArrow);

  let lockX = 1;
  let lockY = 0;
  let hasLock = false;

  return {
    container: layer,

    lockShot(x: number, y: number, _kind: KickKind): void {
      // El tipo ya no hace falta: la mira decide pase o tiro con la carga, igual
      // que el golpeo, y simula el que saldria.
      const len = Math.hypot(x, y) || 1;
      lockX = x / len;
      lockY = y / len;
      hasLock = true;
    },

    clearShotLock(): void {
      hasLock = false;
      shotArrow.visible = false;
      course.visible = false;
    },

    setFirstTime(state): void {
      firstTime = state;
    },

    sync(alpha: number): void {
      const player = target;

      // El anillo vive aparte del resto de la mira: se dibuja siempre que haya
      // ventana, se este cargando o no.
      if (firstTime) {
        const fx = lerp(player.prevPos.x, player.pos.x, alpha);
        const fy = lerp(player.prevPos.y, player.pos.y, alpha);
        const q = Math.max(0, Math.min(1, firstTime.quality));
        // El radio marca el tiempo que queda: el anillo se cierra sobre el pie.
        const progress = Math.max(0, Math.min(1, firstTime.timeToContact / AIM.FIRST_TIME_RING_SPAN));
        const radius = player.radius + 6 + progress * AIM.FIRST_TIME_RING_REACH;
        const perfect = q >= AIM.FIRST_TIME_FLASH_AT;

        firstTimeRing.visible = true;
        firstTimeRing.clear();
        firstTimeRing.circle(0, 0, radius).stroke({
          width: perfect ? 5 : 2.5,
          color: perfect ? COLORS.CHARGE : COLORS.AIM,
          alpha: 0.35 + q * 0.6,
        });
        firstTimeRing.position.set(fx, fy);
      } else if (firstTimeRing.visible) {
        firstTimeRing.visible = false;
      }

      const charging = player.chargeSteer && hasLock;
      root.visible = charging;
      if (!root.visible) {
        shotArrow.visible = false;
        course.visible = false;
        return;
      }

      const px = lerp(player.prevPos.x, player.pos.x, alpha);
      const py = lerp(player.prevPos.y, player.pos.y, alpha);
      root.position.set(px, py);

      const ax = player.aimDir.x;
      const ay = player.aimDir.y;
      const power = humanChargeCurve(player.charge);

      // Flecha de tiro: solo el humano la ve. Mas intensa al redirigir.
      if (charging && hasLock) {
        const dot = clampDot(lockX * ax + lockY * ay);
        const angle = Math.acos(dot);
        const redirected = angle >= AIM.SHOT_REDIRECT_SHOW;
        shotArrow.visible = true;
        /*
         * Lo que saldria si soltaras AHORA: el mismo tipo de golpeo que decide
         * `chargeAndKick` (la carga separa pase de tiro; un pase desde la banda
         * hacia el area es centro) y su vuelo de verdad (`previewKick`).
         */
        let kind: KickKind = player.charge <= KICK.PASS_MAX_CHARGE ? "pass" : "shoot";
        if (kind === "pass" && shouldCross(player, player.aimDir)) kind = "cross";
        const preview = previewKick(
          player,
          {
            kind,
            dir: player.aimDir,
            power: player.charge,
            aiming: true,
            assistSpreadScale: options.assistSpreadScale ?? 1,
          },
          options.ball.radius,
        );
        const arrowLen = shotArrowLength(power, redirected);
        drawShotArrow(shotArrow, player, preview, arrowLen, power, redirected);
        drawCourse(course, preview, arrowLen, kind === "shoot");
      } else {
        shotArrow.visible = false;
        course.visible = false;
      }
    },
  };
}

function clampDot(v: number): number {
  return Math.max(-1, Math.min(1, v));
}

/** Tramos en los que se parte el arco. Suficientes para que no se vea poligonal. */
const ARROW_STEPS = 18;

/**
 * Largo de la flecha para esa carga.
 *
 * Exportado porque la calibracion (`pnpm shot:curve`) necesita saber cuanta
 * curva se ve, y eso depende de cuanto mide la flecha. Con la formula copiada
 * en dos sitios, la cifra que se calibra deja de ser la que se dibuja.
 *
 * El tiro colocado apenas acorta (0,8 y no 0,55): es el que MAS comba, asi que
 * es justo el que necesita linea para enseñarla.
 */
export function shotArrowLength(power: number, redirected: boolean): number {
  return AIM.SHOT_ARROW_LENGTH * lerp(0.8, 1.15, power) * (redirected ? 1.08 : 0.9);
}

/**
 * La trayectoria que va a seguir el balon, como polilinea.
 *
 * `curvature` son radianes por unidad de mundo — la misma cifra con la que el
 * balon se dobla de verdad, salida de `aimPreviewCurvature`. Integrarla paso a
 * paso en vez de dibujar una recta es lo que convierte la comba en una
 * mecanica aprendible: ves la curva ANTES de soltar.
 *
 * Es una funcion pura y exportada a proposito. Que la mira prometa una curva y
 * el balon haga otra es el peor fallo posible aqui —enseña a fallar—, y eso
 * solo se puede vigilar con un test si la geometria se puede llamar sin Pixi.
 */
export function shotArrowPath(
  baseX: number,
  baseY: number,
  dirX: number,
  dirY: number,
  length: number,
  curvature: number,
): { path: number[]; dirX: number; dirY: number } {
  const stepLen = length / ARROW_STEPS;
  const path: number[] = [baseX, baseY];
  let dx = dirX;
  let dy = dirY;
  let cx = baseX;
  let cy = baseY;

  for (let i = 0; i < ARROW_STEPS; i++) {
    const a = curvature * stepLen;
    const c = Math.cos(a);
    const sn = Math.sin(a);
    const ndx = dx * c - dy * sn;
    dy = dx * sn + dy * c;
    dx = ndx;
    cx += dx * stepLen;
    cy += dy * stepLen;
    path.push(cx, cy);
  }

  return { path, dirX: dx, dirY: dy };
}

/**
 * La mira: el primer tramo de la trayectoria de verdad (`previewKick`), del
 * largo de la flecha. Antes se integraba una curvatura aparte, y la flecha
 * ignoraba el golpeo cruzado a la carrera y la curva de colocacion: prometia un
 * disparo y salia otro.
 */
function drawShotArrow(
  g: Graphics,
  player: Player,
  preview: KickPreview,
  length: number,
  power: number,
  redirected: boolean,
): void {
  const { points, tipX, tipY, dirX: dx, dirY: dy } = headOfPath(preview.path, length, player.pos.x, player.pos.y);
  // Las aletas siguen la direccion FINAL, no la de salida: una punta de flecha
  // que apunta a otro sitio que la linea se lee como un error de dibujo.
  const px = -dy;
  const py = dx;
  const head = 16 + power * 14;
  const wing = 9 + power * 6;
  const perfect = player.charge >= KICK.PERFECT_CHARGE;
  const color = perfect ? COLORS.CHARGE : redirected ? COLORS.CHARGE : COLORS.AIM;
  const alpha = perfect ? 0.98 : redirected ? 0.75 + 0.25 * power : 0.4 + 0.25 * power;

  g.clear();
  g.moveTo(points[0]!, points[1]!);
  for (let i = 2; i < points.length; i += 2) g.lineTo(points[i]!, points[i + 1]!);
  g.stroke({
    width: perfect ? 4.5 : redirected ? 3.5 : 2.5,
    color,
    alpha,
    cap: "round",
  });

  g.moveTo(tipX, tipY)
    .lineTo(tipX - dx * head + px * wing, tipY - dy * head + py * wing)
    .lineTo(tipX - dx * head - px * wing, tipY - dy * head - py * wing)
    .closePath()
    .fill({ color, alpha: redirected ? 0.92 : 0.55 });
}

/**
 * El tramo de la trayectoria que cabe en `length`, relativo al jugador, con la
 * direccion de la punta. Exportado para los tests: lo que se dibuja tiene que
 * ser el vuelo, no otra cosa.
 */
export function headOfPath(
  path: readonly number[],
  length: number,
  originX: number,
  originY: number,
): { points: number[]; tipX: number; tipY: number; dirX: number; dirY: number } {
  const points = [path[0]! - originX, path[1]! - originY];
  let walked = 0;
  let dirX = 1;
  let dirY = 0;
  for (let i = 2; i < path.length; i += 2) {
    const sx = path[i]! - path[i - 2]!;
    const sy = path[i + 1]! - path[i - 1]!;
    const seg = Math.hypot(sx, sy);
    if (seg < 1e-6) continue;
    dirX = sx / seg;
    dirY = sy / seg;
    if (walked + seg >= length) {
      const t = (length - walked) / seg;
      points.push(path[i - 2]! + sx * t - originX, path[i - 1]! + sy * t - originY);
      walked = length;
      break;
    }
    walked += seg;
    points.push(path[i]! - originX, path[i + 1]! - originY);
  }
  return { points, tipX: points[points.length - 2]!, tipY: points[points.length - 1]!, dirX, dirY };
}

/**
 * Despues de la flecha: el resto del vuelo punteado, y en la linea de gol una
 * marca de como acaba — dentro (verde), al palo (naranja) o fuera (rojo). Solo
 * en los tiros: un pase no apunta a la porteria.
 */
function drawCourse(g: Graphics, preview: KickPreview, arrowLen: number, shot: boolean): void {
  g.clear();
  if (!shot || preview.outcome === "short") {
    g.visible = false;
    return;
  }
  g.visible = true;
  const path = preview.path;
  const DOT_EVERY = 26;
  let walked = 0;
  let nextDot = arrowLen + DOT_EVERY;
  for (let i = 2; i < path.length; i += 2) {
    const sx = path[i]! - path[i - 2]!;
    const sy = path[i + 1]! - path[i - 1]!;
    const seg = Math.hypot(sx, sy);
    while (seg > 1e-6 && walked + seg >= nextDot) {
      const t = (nextDot - walked) / seg;
      g.circle(path[i - 2]! + sx * t, path[i - 1]! + sy * t, 2.2);
      nextDot += DOT_EVERY;
    }
    walked += seg;
  }
  g.fill({ color: COLORS.AIM, alpha: 0.32 });

  const color = preview.outcome === "goal" ? MARK_GOAL : preview.outcome === "post" ? MARK_POST : MARK_WIDE;
  if (preview.outcome === "wide") {
    const r = 9;
    g.moveTo(preview.x - r, preview.y - r).lineTo(preview.x + r, preview.y + r);
    g.moveTo(preview.x + r, preview.y - r).lineTo(preview.x - r, preview.y + r);
    g.stroke({ width: 3, color, alpha: 0.85, cap: "round" });
    return;
  }
  // Una muesca en la linea de gol y un aro: se lee de un vistazo.
  g.moveTo(preview.x, preview.y - 16).lineTo(preview.x, preview.y + 16);
  g.stroke({ width: 4, color, alpha: 0.9, cap: "round" });
  g.circle(preview.x, preview.y, 11).stroke({ width: 2.5, color, alpha: 0.75 });
}
